import { Checkbox } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import Input from "../../components/Input";
import Button from "../../components/Button";
import { useAppDispatch } from "../../hooks/store";
import { useLogInMutation } from "../../service/auth";
import * as Yup from "yup";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import {
  errorToast,
  getFromStorage,
  removeFromStorage,
  setToStorage,
  successToast,
} from "../../helpers";
import { STORAGE_KEYS } from "../../constants/storageKeys";
import { setCredentials } from "../../reducers/authSlice";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import Loader from "../../constants/Loader";
import { USER_ROLES } from "../../utils/enums";

const sidebarItems = [
  {
    module: 1,
    label: "Dashboard",
    path: "/dashboard",
    icon: "/static/images/dashboard.png",
  },
  {
    module: 2,
    label: "Users",
    icon: "/static/images/user_list.png",
    collapseKey: "users",
    subItems: [
      {
        label: "Individuals",
        path: "/users/individual",
        collapseKey: "users",
      },
      { label: "Companies", path: "/users/company", collapseKey: "users" },
      { label: "Agents", path: "/users/agent", collapseKey: "users" },
    ],
  },
  {
    module: 3,
    label: "Visa Type",
    path: "/visa-type",
    icon: "/static/images/visa.png",
  },
  {
    module: 4,
    label: "Manage Applications",
    path: "/manage-applications",
    icon: "/static/images/form.png",
  },
  {
    module: 5,
    label: "Manage Countries",
    path: "/manage-countries",
    icon: "/static/images/flag.png",
  },
  {
    module: 6,
    label: "Manage Citizenship",
    path: "/manage-citizenship",
    icon: "/static/images/citizenship.png",
  },
  {
    module: 7,
    label: "Manage Documents",
    path: "/manage-documents",
    icon: "/static/images/documents.png",
  },
  {
    module: 8,
    label: "Manage Branches",
    path: "/manage-branches",
    icon: "/static/images/branch.png",
  },
  {
    module: 9,
    label: "Manage Leaves",
    path: "/manage-leaves",
    icon: "/static/images/logout.png",
  },
  {
    module: 10,
    label: "Manage Salaries",
    path: "/manage-salaries",
    icon: "/static/images/salary.png",
  },
  {
    module: 11,
    label: "Manage Payments",
    icon: "/static/images/income.png",
    collapseKey: "payments",
    subItems: [
      { label: "Payment Modes", path: "/manage-payments/payment-modes" },
      { label: "Payment Plans", path: "/manage-payments/payment-plans" },
      { label: "Payments", path: "/manage-payments/payments" },
    ],
  },
  {
    module: 0,
    label: "Manage Sub-admin",
    icon: "/static/images/subadmin.png",
    collapseKey: "subAdmin",
    subItems: [
      { label: "Roles", path: "/user-management/roles" },
      { label: "Sub-admin List", path: "/user-management/user-list" },
    ],
  },
  {
    module: 12,
    label: "Reports and Analytics",
    path: "/reports",
    icon: "/static/images/data-analytics.png",
  },
  {
    module: 13,
    label: "Customer Support",
    path: "/customer-support",
    icon: "/static/images/customer-support.png",
  },
  {
    module: 14,
    label: "Manage Notifications",
    path: "/manage-notifications",
    icon: "/static/images/notification.png",
  },
  {
    module: 15,
    label: "Manage CMS",
    path: "/manage-cms",
    icon: "/static/images/cms.png",
  },
];

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const [loginMutation] = useLogInMutation();

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      remember: false,
    },
    validationSchema: Yup.object({
      remember: Yup.boolean(),
      email: Yup.string()
        .required("Email is required!")
        .matches(
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
          "Please enter a valid email address!"
        ),
      password: Yup.string()
        .required("Password is required")
        .min(6, "Password must be at least 6 characters"),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);
      const tokenInLocalForage = await getFromStorage(STORAGE_KEYS.fcmToken);
      const body = {
        email: values.email,
        password: values.password,
        ...(tokenInLocalForage?.length
          ? { device_token: tokenInLocalForage }
          : {}),
        device_type: "web",
      };
      try {
        setLoading(true);
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await loginMutation(encryptedBody).unwrap();
        setLoading(false);

        if (response?.status === 200) {
          setToStorage(STORAGE_KEYS.token, response?.data?.token || "");
          dispatch(
            setCredentials({
              user: response?.data || null,
              token: response?.data?.token || "",
            })
          );

          if (formik.values.remember) {
            setToStorage(STORAGE_KEYS.credentials, JSON.stringify(body));
          } else {
            removeFromStorage(STORAGE_KEYS.credentials);
          }

          if (response?.data?.user_role_type === USER_ROLES.SUBADMIN) {
            const permissions = response?.data?.role_permission;

            const firstPermissionLabel = permissions.find(
              (item: { can_view: any }) => item?.can_view
            )?.module;
            const hidePermission = permissions?.find(
              (item: { can_view: any }) => item?.can_view
            );

            const redirectPath = sidebarItems?.find(
              (item) => item?.label === firstPermissionLabel
            )?.path;

            if (redirectPath) {
              navigate(redirectPath, { state: hidePermission });
              successToast("Logged In Successfully.");
            } else {
              errorToast("No permissions found for this user");
            }
          } else {
            navigate("/dashboard", { replace: true });
            successToast("Logged In Successfully.");
          }
        }
      } catch (error: any) {
        errorToast(error?.data?.message || "");
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const data = getFromStorage(STORAGE_KEYS.credentials);
    if (data) {
      const rememberData = JSON.parse(`${data}`);
      formik.setFieldValue("password", rememberData?.password);
      formik.setFieldValue("email", rememberData?.email);
      formik.setFieldValue("remember", true);
    }
  }, []);

  return (
    <AuthLayout>
      <Loader isLoad={loading} />
      <div className="authBox">
        <h2>Sign In</h2>
        <p>Welcome to Wizard Admin</p>
        <form onSubmit={formik.handleSubmit}>
          <Input
            placeholder="Email"
            name="email"
            id="email"
            inputProps={{ maxLength: 80 }}
            value={formik.values.email}
            onChange={(val) => {
              if (val.target.value === " " || val.target.value === ".") {
                return;
              } else {
                formik.handleChange(val);
              }
            }}
            onBlur={formik.handleBlur}
            helperText={formik.touched.email && formik.errors.email}
          />
          <Input
            placeholder="Password"
            password
            id="password"
            name="password"
            value={formik.values.password}
            onChange={(val) => {
              if (val.target.value === " " || val.target.value === ".") {
                return;
              } else {
                formik.handleChange(val);
              }
            }}
            onBlur={formik.handleBlur}
            helperText={formik.touched.password && formik.errors.password}
          />
          <div className="form_control d_flex">
            <div className="remember_box">
              <FormControlLabel
                control={<Checkbox />}
                checked={formik.values.remember}
                name="remember"
                onChange={formik.handleChange}
                label="Remember me"
              />
            </div>
            <a
              href="javacript:void(0)"
              className="anchor_link"
              onClick={() => navigate("/forgot-password")}
            >
              Forgot Password?
            </a>
          </div>

          <Button value="Sign In" type="submit" />
        </form>
      </div>
    </AuthLayout>
  );
};

export default Login;
